import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Globe, Upload, Loader2, History, ShieldX, AlertTriangle,
  FileSpreadsheet, RefreshCw, CheckCircle2, ArrowLeft, ShieldAlert,
  Download, TrendingDown, TrendingUp, Minus, Undo2, Paperclip, Clock,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { fdate } from "@/lib/format";
import { ROLES_LISTAS } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// sep-2026 · LIMPIEZA VISUAL (mandato del PO): la pantalla ya NO pinta semáforo de frescura,
// alarmas por antigüedad, umbrales ni avisos de demostración. El backend conserva su lógica
// (`semaforo_frescura`, `umbral_ambar`, `alerta_critica`, `/screening/listas-config`), pero la
// vista solo informa: nombre, versión, registros y última carga. La carga, el versionado, el
// re-escaneo y el blindaje de publicación no se tocaron.

export default function CentroListas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [estado, setEstado] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [reescaneos, setReescaneos] = useState([]);
  const [publicaciones, setPublicaciones] = useState({});
  const [reescaneo, setReescaneo] = useState(null);
  const [enCola, setEnCola] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Mapa nombre_lista → versión vigente completa (para "Descargar vigente" y reversión).
  const vigentePorLista = useMemo(() => {
    const map = {};
    (historial || []).forEach((h) => {
      if (h.vigente && h.nombre_lista && h.id) map[h.nombre_lista] = h;
    });
    return map;
  }, [historial]);
  const vigenteIdPorLista = useMemo(() => {
    const map = {};
    Object.entries(vigentePorLista).forEach(([n, h]) => { map[n] = h.id; });
    return map;
  }, [vigentePorLista]);
  const [revirtiendo, setRevirtiendo] = useState("");

  // Carga / vista previa
  const [upOpen, setUpOpen] = useState(false);
  const [step, setStep] = useState("form");
  const [nombreLista, setNombreLista] = useState("");
  const [version, setVersion] = useState("");
  const [fechaPub, setFechaPub] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [confirmoReduccion, setConfirmoReduccion] = useState(false);

  const load = useCallback(async () => {
    try {
      const [e, h, r, p] = await Promise.all([
        api.get("/screening/listas-estado"),
        api.get("/screening/listas-globales"),
        api.get("/screening/reescaneos"),
        api.get("/screening/listas-publicaciones"),
      ]);
      setEstado(e.data); setHistorial(h.data); setReescaneos(r.data);
      setPublicaciones(p.data?.por_lista || {});
      setReescaneo(p.data?.reescaneo || null);
      setEnCola(p.data?.reescaneo_en_cola || 0);
      return p.data?.en_curso || 0;
    } catch (err) { setError(true); return 0; } finally { setLoading(false); }
  }, []);

  // sep-2026 · El re-escaneo corre en segundo plano: la tarjeta se refresca sola mientras
  // haya publicaciones «publicando» para pasar a «Re-escaneo terminado» con sus conteos.
  // oct-2026 · También mientras haya un re-escaneo corriendo o esperando turno: el de una
  // reversión no tiene publicación que lo represente.
  const [sondeando, setSondeando] = useState(false);
  useEffect(() => {
    if (!sondeando) return undefined;
    const t = setInterval(async () => {
      const enCurso = await load();
      if (!enCurso) setSondeando(false);
    }, 4000);
    return () => clearInterval(t);
  }, [sondeando, load]);

  useEffect(() => {
    if (user && !ROLES_LISTAS.includes(user.role)) { setError(true); setLoading(false); return; }
    if (user) load();
  }, [user, load]);

  const openUpload = (lista) => {
    setNombreLista(lista || (estado[0]?.nombre_lista ?? ""));
    setVersion(""); setFechaPub(""); setFile(null); setParsed(null);
    setConfirmoReduccion(false); setStep("form"); setUpOpen(true);
  };

  const doParse = async () => {
    if (!file) { toast.error("Selecciona un archivo CSV, Excel o XML"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("nombre_lista", nombreLista);
      fd.append("archivo", file);
      // Un solo request al endpoint /diff que ya hace el parse Y la comparación
      // contra la versión vigente (endpoint nuevo aditivo).
      const { data } = await api.post("/screening/listas-globales/diff", fd);
      // El backend retorna `registros` en la respuesta — reusamos ese payload al confirmar.
      setParsed({
        ...data,
        total_detectado: data.total_nuevo,
        preview: (data.registros || []).slice(0, 10),
        formato: file.name.toLowerCase().endsWith(".xml") ? "XML" :
                 file.name.toLowerCase().endsWith(".csv") ? "CSV" : "Excel",
      });
      setConfirmoReduccion(false);
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo leer el archivo");
    } finally { setBusy(false); }
  };

  const descargarPlantilla = async (lista) => {
    try {
      const resp = await api.get("/screening/listas-globales/plantilla",
        { params: { lista }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      const safe = lista.toLowerCase().replace(/[^a-z0-9]/g, "_");
      a.download = `plantilla_${safe}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error(_e?.response?.data?.detail || "No se pudo descargar la plantilla");
    }
  };

  const descargarVigente = async (listaId, nombre) => {
    try {
      const resp = await api.get(`/screening/listas-globales/${listaId}/export`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      const safe = (nombre || "lista").toLowerCase().replace(/[^a-z0-9]/g, "_");
      a.download = `${safe}_vigente.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error("No se pudo descargar la versión vigente");
    }
  };

  const doConfirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/screening/listas-globales/version", {
        nombre_lista: nombreLista, version: version || undefined,
        fecha_publicacion: fechaPub || undefined, formato: parsed.formato, registros: parsed.registros,
        // sep-2026 · la publicación exige la firma del dry-run; si el archivo se encoge
        // ≥20 % además exige la confirmación explícita del Super Administrador.
        firma: parsed.firma,
        confirmo_reduccion: parsed.requiere_confirmacion ? confirmoReduccion : undefined,
      });
      // sep-2026 · La respuesta ya NO espera el re-escaneo (Cloudflare cortaba la petición
      // con archivos grandes). Idempotente: un reintento devuelve la MISMA versión.
      if (data.duplicado_evitado) {
        toast.info(data.mensaje || "Esta versión ya estaba publicada: no se duplicó.");
      } else if (data.estado === "programado") {
        toast.success(`Publicada: ${data.registros} registro(s). Los registros nuevos se cotejan a las 02:00.`);
      } else if (data.reescaneo_aplicable && data.estado !== "terminado") {
        toast.success(`Publicada: ${data.registros} registro(s). Re-escaneo en curso…`);
        setSondeando(true);
      } else {
        toast.success(`Publicada: ${data.registros} registro(s).`);
      }
      setUpOpen(false);
      const enCurso = await load();
      if (enCurso) setSondeando(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la versión");
    } finally { setBusy(false); }
  };

  const descargarDescartadas = async () => {
    try {
      const resp = await api.get(`/screening/listas-carga/${parsed.dryrun_id}/descartadas`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `descartadas_${nombreLista.toLowerCase().replace(/[^a-z0-9]/g, "_")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error("No se pudieron descargar las filas descartadas");
    }
  };

  const descargarEvidencia = async (lista) => {
    try {
      const resp = await api.get(`/screening/listas-globales/${lista.id}/evidencia`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = lista.archivo_nombre || `evidencia_${lista.id}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error(_e?.response?.data?.detail || "No se pudo descargar el archivo fuente");
    }
  };

  const revertir = async (lista) => {
    if (!window.confirm(
      `¿Revertir la carga de ${lista.nombre_lista} v${lista.version}?\n\n` +
      "Se restaura la versión anterior, se anulan las coincidencias pendientes que esta " +
      "carga generó, se cierran sus alertas y se liberan los bloqueos que provocó. Las " +
      "coincidencias que el Oficial de Cumplimiento ya decidió se conservan como evidencia."
    )) return;
    setRevirtiendo(lista.id);
    try {
      const { data } = await api.post(`/screening/listas-globales/${lista.id}/revertir`);
      toast.success(
        `Restaurada v${data.restaurada.version}: ${data.coincidencias_anuladas} coincidencia(s) ` +
        `anulada(s), ${data.alertas_cerradas} alerta(s) cerrada(s), ` +
        `${data.bloqueos_liberados} bloqueo(s) liberado(s).` +
        (data.recotejo === "en_curso"
          ? " El re-cotejo corre en segundo plano: puedes seguir trabajando."
          : "")
      );
      if (data.recotejo === "en_curso") setSondeando(true);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revertir la carga");
    } finally { setRevirtiendo(""); }
  };

  const cotejarAhora = async (nombreLista) => {
    const pub = publicaciones[nombreLista];
    if (!pub?.id) return;
    try {
      await api.post(`/screening/listas-publicaciones/${pub.id}/reescanear-ahora`);
      toast.success("Cotejo iniciado");
      await load();
      setSondeando(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (error)
    return (
      <div>
        <PageHeader title="Centro de Listas" subtitle="Panel de plataforma" />
        <div className="flex flex-col items-center justify-center gap-3 p-16 text-slate-400">
          <ShieldX className="h-10 w-10" />
          <p className="text-sm">Acceso exclusivo del Super Administrador de la plataforma.</p>
        </div>
      </div>
    );

  return (
    <div data-testid="centro-listas">
      <PageHeader title="Centro de Listas PLD"
        subtitle="Carga, versionado y re-escaneo de listas globales de sanciones">
        {/* sep-2026 · La Lista de Vigilancia es un MÓDULO APARTE (no está en el alcance del
            rol «Cargador de Listas»): a él no se le ofrece el atajo. */}
        {user?.role === "super_admin" && (
          <Button data-testid="btn-lista-vigilancia" variant="outline" size="sm"
            onClick={() => navigate("/admin-vigilancia")}
            className="border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
            <ShieldAlert className="mr-1.5 h-4 w-4" /> Lista de Vigilancia FELVORA
          </Button>
        )}
        <Button data-testid="cargar-version-btn" size="sm" className="bg-primary hover:bg-sidebar-hover" onClick={() => openUpload()}>
          <Upload className="mr-1.5 h-4 w-4" /> Cargar nueva versión
        </Button>
      </PageHeader>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-6 p-6 md:p-8">
          {/* oct-2026 · El re-escaneo corre en un proceso aparte y de uno en uno: aquí se ve
              cuál está corriendo y cuántos esperan turno. */}
          {reescaneo && (
            <div data-testid="reescaneo-en-curso"
              className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
              <Loader2 className="h-4 w-4 animate-spin text-sky-600" />
              <b>
                {reescaneo.motivo === "reversion"
                  ? `Re-cotejo por reversión de ${reescaneo.nombre_lista}`
                  : `Re-escaneo de ${reescaneo.nombre_lista} v${reescaneo.version}`}
              </b>
              <span data-testid="reescaneo-avance">
                · {reescaneo.entidades_reescaneadas ?? 0} de {reescaneo.total_entidades ?? 0} entidad(es)
                {" "}· {reescaneo.coincidencias_nuevas ?? 0} coincidencia(s) nueva(s)
              </span>
              <span className="text-sky-700">
                — corre en segundo plano; puedes seguir trabajando.
              </span>
              {enCola > 0 && (
                <span data-testid="reescaneo-en-cola"
                  className="rounded bg-sky-200 px-1.5 py-0.5 text-[11px] font-medium text-sky-900">
                  {enCola} esperando turno
                </span>
              )}
            </div>
          )}

          {/* Listas globales cargadas — informativo, sin semáforo ni umbrales */}
          <div>
            <h3 className="mb-3 flex items-center gap-1.5 font-heading text-sm font-semibold text-slate-700"><Globe className="h-4 w-4 text-primary" /> Listas globales</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="estado-frescura-grid">
              {estado.map((e) => (
                <div key={e.nombre_lista} data-testid={`estado-${e.nombre_lista}`} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="truncate font-heading text-sm font-semibold text-slate-900">{e.nombre_lista}</p>
                  {/* sep-2026 · estado de la publicación: el re-escaneo ya no bloquea la
                      respuesta, así que la tarjeta informa su avance y su resultado.
                      oct-2026 · «programado» = los registros nuevos se cotejan en la
                      ventana nocturna; «Cotejar ahora» lo adelanta bajo demanda. */}
                  {publicaciones[e.nombre_lista] && (
                    <>
                      <p data-testid={`publicacion-${e.nombre_lista}`}
                        className={`mt-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                          publicaciones[e.nombre_lista].estado === "error"
                            ? "bg-red-100 text-red-800"
                            : ["programado", "publicando"].includes(publicaciones[e.nombre_lista].estado)
                              ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                        {publicaciones[e.nombre_lista].estado === "programado" ? (
                          <><Clock className="h-3 w-3" /> Publicada — registros nuevos se cotejan a las 02:00 (CDMX)</>
                        ) : publicaciones[e.nombre_lista].estado === "publicando" ? (
                          <><Loader2 className="h-3 w-3 animate-spin" /> Publicada — re-escaneo en curso</>
                        ) : publicaciones[e.nombre_lista].estado === "error" ? (
                          <><AlertTriangle className="h-3 w-3" /> Error al publicar</>
                        ) : publicaciones[e.nombre_lista].reescaneo_aplicable ? (
                          <><CheckCircle2 className="h-3 w-3" /> Re-escaneo terminado ·{" "}
                            {publicaciones[e.nombre_lista].entidades_reescaneadas ?? 0} entidad(es) ·{" "}
                            {publicaciones[e.nombre_lista].coincidencias_nuevas ?? 0} coincidencia(s) nueva(s)
                            {publicaciones[e.nombre_lista].registros_nuevos != null &&
                              ` · ${publicaciones[e.nombre_lista].registros_nuevos} registro(s) nuevo(s)`}</>
                        ) : (
                          <><CheckCircle2 className="h-3 w-3" /> Publicada (lista no crítica: sin re-escaneo)</>
                        )}
                      </p>
                      {publicaciones[e.nombre_lista].estado === "programado" && (
                        <Button data-testid={`reescanear-ahora-${e.nombre_lista}`} variant="outline" size="sm"
                          className="mt-1.5 h-6 w-full text-[11px]"
                          onClick={() => cotejarAhora(e.nombre_lista)}>
                          <RefreshCw className="mr-1 h-3 w-3" /> Cotejar ahora
                        </Button>
                      )}
                    </>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div><span className="text-slate-400">Versión</span><br /><b>{e.version || "—"}</b></div>
                    <div><span className="text-slate-400">Registros</span><br /><b>{e.total_registros ?? 0}</b></div>
                    <div className="col-span-2" data-testid={`ultima-carga-${e.nombre_lista}`}>
                      <span className="text-slate-400">Última carga</span><br />
                      <b>{e.fecha_carga ? fdate(e.fecha_carga) : (e.fecha_publicacion ? fdate(e.fecha_publicacion) : "—")}</b>
                    </div>
                  </div>
                  <Button data-testid={`actualizar-${e.nombre_lista}`} variant="outline" size="sm" className="mt-3 w-full" onClick={() => openUpload(e.nombre_lista)}>
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Actualizar
                  </Button>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Button
                      data-testid={`plantilla-${e.nombre_lista}`}
                      variant="ghost"
                      size="sm"
                      className="h-8 text-[11px] text-slate-600 hover:bg-slate-100"
                      onClick={() => descargarPlantilla(e.nombre_lista)}
                    >
                      <FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> Plantilla
                    </Button>
                    <Button
                      data-testid={`export-${e.nombre_lista}`}
                      variant="ghost"
                      size="sm"
                      className="h-8 text-[11px] text-slate-600 hover:bg-slate-100"
                      disabled={!vigenteIdPorLista[e.nombre_lista]}
                      onClick={() => descargarVigente(vigenteIdPorLista[e.nombre_lista], e.nombre_lista)}
                    >
                      <Download className="mr-1 h-3.5 w-3.5" /> Vigente
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Traza de re-escaneo */}
          <div>
            <h3 className="mb-3 flex items-center gap-1.5 font-heading text-sm font-semibold text-slate-700"><RefreshCw className="h-4 w-4 text-forest-600" /> Traza de re-escaneo automático (agregado, sin datos personales)</h3>
            <DataTable testid="table-reescaneos" rows={reescaneos}
              columns={[
                { key: "fecha", label: "Fecha", kind: "date" },
                { key: "nombre_lista", label: "Lista" },
                { key: "version", label: "Versión" },
                { key: "entidades_reescaneadas", label: "Entidades re-escaneadas" },
                { key: "coincidencias_nuevas", label: "Coincidencias nuevas", render: (r) => <span className={`font-semibold tabular-nums ${r.coincidencias_nuevas > 0 ? "text-amber-600" : "text-slate-600"}`}>{r.coincidencias_nuevas}</span> },
                { key: "usuario", label: "Ejecutó" },
              ]} emptyLabel="Aún no se han registrado re-escaneos automáticos" />
          </div>

          {/* Historial de versiones */}
          <div>
            <h3 className="mb-3 flex items-center gap-1.5 font-heading text-sm font-semibold text-slate-700"><History className="h-4 w-4 text-slate-500" /> Historial de versiones</h3>
            <DataTable testid="table-listas-globales-admin" rows={historial}
              columns={[
                { key: "nombre_lista", label: "Lista" },
                { key: "version", label: "Versión" },
                { key: "fecha_publicacion", label: "Publicación", render: (r) => <span className="text-slate-500">{r.fecha_publicacion ? fdate(r.fecha_publicacion) : "—"}</span> },
                { key: "fecha_carga", label: "Carga", kind: "date" },
                { key: "total_registros", label: "Registros" },
                { key: "formato", label: "Formato", kind: "badge" },
                { key: "hash_version", label: "Hash", render: (r) => <span className="font-mono text-xs text-slate-500">{String(r.hash_version || "").slice(0, 10)}</span> },
                { key: "vigente", label: "Estatus", render: (r) => <StatusBadge value={r.vigente ? "Vigente" : "Histórica"} /> },
                { key: "acciones", label: "Evidencia y reversión", render: (r) => (
                  <div className="flex items-center gap-1">
                    <Button data-testid={`evidencia-${r.id}`} variant="ghost" size="sm"
                      className="h-7 px-2 text-[11px] text-slate-600 hover:bg-slate-100"
                      disabled={!r.archivo_evidencia}
                      title={r.archivo_evidencia ? "Descargar el archivo fuente tal como se subió"
                                                  : "Esta versión no conserva archivo fuente"}
                      onClick={() => descargarEvidencia(r)}>
                      <Paperclip className="mr-1 h-3 w-3" /> Fuente
                    </Button>
                    {r.vigente && (
                      <Button data-testid={`revertir-${r.id}`} variant="ghost" size="sm"
                        className="h-7 px-2 text-[11px] text-red-600 hover:bg-red-50"
                        disabled={revirtiendo === r.id}
                        title="Restaurar la versión anterior y deshacer los efectos de esta carga"
                        onClick={() => revertir(r)}>
                        {revirtiendo === r.id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Undo2 className="mr-1 h-3 w-3" />} Revertir
                      </Button>
                    )}
                  </div>
                ) },
              ]} emptyLabel="Sin listas globales" />
            <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400"><History className="h-3.5 w-3.5" /> Cada versión conserva su hash SHA-256 y usuario para trazabilidad. La LPB es de carga manual.</p>
          </div>
        </div>
      )}

      {/* Diálogo de carga con vista previa */}
      <Dialog open={upOpen} onOpenChange={setUpOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="cargar-version-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {step === "form" ? "Cargar nueva versión de lista" : "Vista previa y confirmación"}
            </DialogTitle>
            <DialogDescription className="sr-only">Carga de lista global con vista previa</DialogDescription>
          </DialogHeader>

          {step === "form" ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-xs">Lista</Label>
                  <Select value={nombreLista} onValueChange={setNombreLista}>
                    <SelectTrigger data-testid="cv-lista"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>{estado.map((e) => <SelectItem key={e.nombre_lista} value={e.nombre_lista}>{e.nombre_lista}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Versión (opcional)</Label>
                  <Input data-testid="cv-version" placeholder="2026.06.20" value={version} onChange={(e) => setVersion(e.target.value)} />
                </div>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Fecha de publicación / corte de la fuente</Label>
                <Input data-testid="cv-fecha-pub" type="date" value={fechaPub} onChange={(e) => setFechaPub(e.target.value)} />
                <p className="mt-1 text-[11px] text-slate-400">Fecha en que la autoridad publicó la lista. Si se omite, se usa la fecha de carga.</p>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Archivo (CSV, Excel .xlsx o XML)</Label>
                <Input data-testid="cv-archivo" type="file" accept=".csv,.xlsx,.xls,.xml,.txt"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
                <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-600"><AlertTriangle className="h-3 w-3" /> El PDF no se admite: convierte a CSV/Excel (o XML para OFAC/ONU) antes de cargar.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="preview-panel">
              {/* sep-2026 · AVISO (no bloquea): el archivo parece de otra lista del catálogo
                  — p. ej. el listado del SAT 69-B cargado en la tarjeta de SAT 69. */}
              {parsed?.aviso_lista_cruzada && (
                <div
                  className="flex items-start gap-2 rounded-lg border-2 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  data-testid="aviso-lista-cruzada"
                >
                  <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <div className="min-w-0">
                    <p className="font-bold uppercase tracking-wide">
                      ¿Tarjeta equivocada? Esto parece «{parsed.aviso_lista_cruzada.lista_probable}»
                    </p>
                    <p className="mt-1 text-amber-800">{parsed.aviso_lista_cruzada.mensaje}</p>
                  </div>
                </div>
              )}

              {parsed?.warning && (
                <div
                  className="flex items-start gap-2 rounded-lg border-2 border-red-400 bg-red-50 px-4 py-3 text-sm text-red-900"
                  data-testid="diff-warning-shrinkage"
                >
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                  <div>
                    <p className="font-bold uppercase tracking-wide">Atención — posible archivo incompleto</p>
                    <p className="mt-1 text-red-800">{parsed.warning}</p>
                  </div>
                </div>
              )}

              {/* Resumen del diff */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="diff-summary">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-center">
                  <TrendingUp className="mx-auto mb-1 h-4 w-4 text-emerald-700" />
                  <p className="text-xs text-emerald-800">Entran</p>
                  <p data-testid="diff-entran" className="text-lg font-bold text-emerald-800 tabular-nums">
                    +{parsed?.entran_total ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-center">
                  <TrendingDown className="mx-auto mb-1 h-4 w-4 text-red-700" />
                  <p className="text-xs text-red-800">Salen</p>
                  <p data-testid="diff-salen" className="text-lg font-bold text-red-800 tabular-nums">
                    −{parsed?.salen_total ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center">
                  <Minus className="mx-auto mb-1 h-4 w-4 text-slate-600" />
                  <p className="text-xs text-slate-600">Siguen igual</p>
                  <p data-testid="diff-siguen" className="text-lg font-bold text-slate-700 tabular-nums">
                    {parsed?.siguen_total ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-center">
                  <FileSpreadsheet className="mx-auto mb-1 h-4 w-4 text-indigo-700" />
                  <p className="text-xs text-indigo-800">Total nuevo vs vigente</p>
                  <p data-testid="diff-totales" className="text-sm font-bold text-indigo-800 tabular-nums">
                    {parsed?.total_nuevo ?? 0} vs {parsed?.total_vigente ?? 0}
                  </p>
                </div>
              </div>

              {parsed?.version_vigente && (
                <p className="text-xs text-slate-500">
                  Comparando contra versión vigente <b>v{parsed.version_vigente.version}</b>
                  {parsed.version_vigente.fecha_publicacion &&
                    ` · publicada ${fdate(parsed.version_vigente.fecha_publicacion)}`}
                </p>
              )}

              {/* sep-2026 · VISTA PREVIA — cómo se leyó el archivo y las primeras 10 filas
                  ya interpretadas, para confirmar ANTES de publicar a las entidades. */}
              <div className="min-w-0 rounded-lg border border-slate-200 bg-white" data-testid="preview-lectura">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                  <p className="text-xs font-semibold text-slate-700">
                    Vista previa · <span data-testid="preview-total" className="tabular-nums">{parsed?.total_detectado ?? parsed?.total_nuevo ?? 0}</span> registro(s) detectado(s)
                    <span className="text-slate-400"> — se muestran los primeros {Math.min(10, parsed?.preview?.length || 0)}</span>
                  </p>
                  <p className="text-[11px] text-slate-500" data-testid="preview-encabezado-fila">
                    {parsed?.lectura?.formato || "—"}
                    {parsed?.lectura?.fila_encabezado
                      ? ` · encabezados leídos en la fila ${parsed.lectura.fila_encabezado}`
                      : " · sin encabezados (por nodos)"}
                    {parsed?.lectura?.filas_titulo_ignoradas > 0 &&
                      ` · se ignoró ${parsed.lectura.filas_titulo_ignoradas} fila(s) de título`}
                  </p>
                </div>
                {Object.keys(parsed?.lectura?.columnas_mapeadas || {}).length > 0 && (
                  <div className="flex flex-wrap gap-1 border-b border-slate-100 px-3 py-2" data-testid="preview-columnas">
                    {Object.entries(parsed.lectura.columnas_mapeadas).map(([orig, canon]) => (
                      <span key={orig} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                        {orig} → <b className="text-slate-800">{canon}</b>
                      </span>
                    ))}
                  </div>
                )}
                <div className="max-h-56 min-w-0 overflow-auto">
                  <table className="w-full text-left text-xs" data-testid="preview-filas">
                    <thead className="sticky top-0 bg-slate-50 text-slate-500">
                      <tr>
                        <th className="px-2 py-1 font-medium">#</th>
                        <th className="px-2 py-1 font-medium">Nombre / Razón social</th>
                        <th className="px-2 py-1 font-medium">RFC</th>
                        <th className="px-2 py-1 font-medium">Estatus</th>
                        <th className="px-2 py-1 font-medium">Programa / Supuesto</th>
                        <th className="px-2 py-1 font-medium">Alias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(parsed?.preview || []).map((r, i) => (
                        <tr key={i} data-testid={`preview-fila-${i}`} className="border-t border-slate-100">
                          <td className="px-2 py-1 tabular-nums text-slate-400">{i + 1}</td>
                          <td className="px-2 py-1 font-medium text-slate-800">{r.nombre}</td>
                          <td className="px-2 py-1 font-mono text-slate-600">{r.rfc || "—"}</td>
                          <td className="px-2 py-1 text-slate-700" data-testid={`preview-estatus-${i}`}>
                            {r.estatus_fiscal || "—"}
                          </td>
                          <td className="px-2 py-1 text-slate-600">{r.programa || "—"}</td>
                          <td className="px-2 py-1 text-slate-500">{r.alias || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* sep-2026 · punto 3 del PO — LO QUE NO ENTRA, con motivo y descargable.
                  Invariante visible: filas leídas = cargados + descartadas. */}
              {parsed?.descartes_aplicable && (
                <div className="rounded-lg border border-slate-200 bg-white p-3"
                  data-testid="preview-descartes">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-slate-700">
                      Filas del archivo: <b className="tabular-nums">{parsed.filas_archivo}</b>
                      {" = "}cargadas <b data-testid="descartes-cargados" className="tabular-nums">{parsed.cargados}</b>
                      {" + "}
                      <b data-testid="descartes-total"
                        className={`tabular-nums ${parsed.descartadas_total > 0 ? "text-red-700" : ""}`}>
                        {parsed.descartadas_total}
                      </b>{" "}descartada(s)
                    </p>
                    {parsed.descartadas_total > 0 && parsed.dryrun_id && (
                      <Button data-testid="descargar-descartadas" variant="outline" size="sm"
                        className="h-7 text-[11px]" onClick={descargarDescartadas}>
                        <Download className="mr-1 h-3.5 w-3.5" /> Descargar descartadas
                      </Button>
                    )}
                  </div>
                  {parsed.descartadas_total > 0 ? (
                    <>
                      <ul className="mt-2 list-inside list-disc text-[11px] text-red-800">
                        {Object.entries(parsed.descartes_motivos || {}).map(([m, n]) => (
                          <li key={m} data-testid="descartes-motivo">{n} × {m}</li>
                        ))}
                      </ul>
                      <div className="mt-2 max-h-32 overflow-auto rounded border border-red-100">
                        <table className="w-full text-left text-[11px]" data-testid="descartes-tabla">
                          <thead className="bg-red-50 text-red-800">
                            <tr><th className="px-2 py-1">Fila</th><th className="px-2 py-1">Motivo</th><th className="px-2 py-1">Contenido leído</th></tr>
                          </thead>
                          <tbody>
                            {(parsed.descartadas || []).map((d, i) => (
                              <tr key={i} className="border-t border-red-50">
                                <td className="px-2 py-1 tabular-nums text-slate-500">{d.fila_archivo ?? "—"}</td>
                                <td className="px-2 py-1 text-slate-700">{d.motivo}</td>
                                <td className="px-2 py-1 text-slate-500">{d.contenido || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <p className="mt-1 text-[11px] text-emerald-700" data-testid="descartes-cero">
                      Ninguna fila se descartó: todas las filas con nombre o razón social entraron.
                    </p>
                  )}
                  {parsed.duplicados_total > 0 && (
                    <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800"
                      data-testid="descartes-duplicados">
                      <b>{parsed.duplicados_total}</b> fila(s) repiten el RFC/CURP de otra del mismo
                      archivo. <b>Sí se guardan</b> (cada oficio o estatus es evidencia), pero la
                      comparación de arriba cuenta a cada persona una sola vez: por eso
                      «siguen igual» puede ser menor que el total cargado.
                    </p>
                  )}
                  {parsed.sin_rfc_total > 0 && (
                    <p className="mt-1 text-[11px] text-slate-500" data-testid="descartes-sin-rfc">
                      {parsed.sin_rfc_total} registro(s) sin RFC: se guardan y cotejan por nombre.
                    </p>
                  )}
                </div>
              )}

              {/* Listado que ENTRA */}
              {parsed?.entran?.length > 0 && (
                <div>
                  <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-emerald-700">
                    <TrendingUp className="h-3.5 w-3.5" /> Registros que ENTRAN ({parsed.entran_total})
                    {parsed?.entran_truncado && <span className="text-slate-500">— mostrando primeros 200</span>}
                  </p>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-emerald-100">
                    <table className="w-full text-left text-xs" data-testid="diff-entran-table">
                      <thead className="bg-emerald-50 text-emerald-800">
                        <tr>
                          <th className="px-3 py-1.5">Nombre / Razón social</th>
                          <th className="px-3 py-1.5">RFC / CURP</th>
                          <th className="px-3 py-1.5">Programa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.entran.map((r, i) => (
                          <tr key={i} className="border-t border-emerald-50">
                            <td className="px-3 py-1.5 text-slate-800">+ {r.nombre}</td>
                            <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500">{r.rfc || r.curp || "—"}</td>
                            <td className="px-3 py-1.5 text-slate-500">{r.programa || r.estatus_fiscal || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Listado que SALE */}
              {parsed?.salen?.length > 0 && (
                <div>
                  <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-red-700">
                    <TrendingDown className="h-3.5 w-3.5" /> Registros que SALEN ({parsed.salen_total})
                    {parsed?.salen_truncado && <span className="text-slate-500">— mostrando primeros 200</span>}
                  </p>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-red-100">
                    <table className="w-full text-left text-xs" data-testid="diff-salen-table">
                      <thead className="bg-red-50 text-red-800">
                        <tr>
                          <th className="px-3 py-1.5">Nombre / Razón social</th>
                          <th className="px-3 py-1.5">RFC / CURP</th>
                          <th className="px-3 py-1.5">Programa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.salen.map((r, i) => (
                          <tr key={i} className="border-t border-red-50">
                            <td className="px-3 py-1.5 text-slate-800">− {r.nombre}</td>
                            <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500">{r.rfc || r.curp || "—"}</td>
                            <td className="px-3 py-1.5 text-slate-500">{r.programa || r.estatus_fiscal || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                Al confirmar se reemplazará la versión vigente de <b>{nombreLista}</b>. Las versiones
                anteriores quedan en el historial. Si es lista crítica, el re-escaneo de la cartera de
                todas las entidades corre <b>en segundo plano</b>: la publicación te responde de
                inmediato y la tarjeta te avisa cuando el re-escaneo termina.
              </p>

              {parsed?.requiere_confirmacion && (
                <label data-testid="confirmar-reduccion-wrap"
                  className="flex cursor-pointer items-start gap-2.5 rounded-lg border-2 border-red-300 bg-white px-3 py-2.5">
                  <Checkbox data-testid="confirmar-reduccion" className="mt-0.5"
                    checked={confirmoReduccion}
                    onCheckedChange={(v) => setConfirmoReduccion(v === true)} />
                  <span className="text-xs text-slate-700">
                    <b className="text-red-700">Confirmación obligatoria:</b> revisé el detalle y
                    confirmo que la reducción de {parsed.shrinkage_pct}% es correcta
                    ({parsed.total_nuevo} registros contra {parsed.total_vigente} vigentes). Entiendo
                    que el efecto es inmediato en todas las entidades.
                  </span>
                </label>
              )}
              <p className="text-[11px] text-slate-400">
                El archivo que subiste queda guardado como evidencia de esta versión y la carga se
                puede revertir desde el historial.
              </p>
            </div>
          )}

          <DialogFooter>
            {step === "form" ? (
              <>
                <Button variant="outline" onClick={() => setUpOpen(false)}>Cancelar</Button>
                <Button data-testid="cv-parse" onClick={doParse} disabled={busy || !file || !nombreLista} className="bg-primary hover:bg-sidebar-hover">
                  {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Validar archivo
                </Button>
              </>
            ) : (
              <>
                <Button data-testid="cv-volver" variant="outline" onClick={() => setStep("form")}><ArrowLeft className="mr-1.5 h-4 w-4" /> Volver</Button>
                <Button data-testid="cv-confirmar" onClick={doConfirm}
                  disabled={busy || (parsed?.requiere_confirmacion && !confirmoReduccion)}
                  className="bg-accent hover:bg-forest-700">
                  {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />} Confirmar y reemplazar versión
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
